import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/app/AppShell";
import { FullPageLoading } from "../../components/Loading";
import {
  getChapter,
  listChapters,
  listProfiles,
  type ChapterDetail,
  type ChapterSummary,
} from "../../lib/api";
import {
  chapterNumber,
  chapterTitleWithoutNumber,
  toRoman,
} from "../../lib/format";

// Customer-facing plain-text book view. Fetches every chapter that has
// content for the user's single storyteller and renders the manuscript
// chapter by chapter, with an "Alles kopieren"-shortcut.

export default function AppBookText() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [chapters, setChapters] = useState<
    { num: number; summary: ChapterSummary; detail: ChapterDetail }[]
  >([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const profilesResp = await listProfiles();
        const p = (profilesResp.profiles || [])[0];
        if (!p) {
          setLoading(false);
          return;
        }
        setName(p.interviewee_name || "");

        const ch = await listChapters(p.phone);
        const sorted = [...(ch.chapters || [])].sort(
          (a, b) => chapterNumber(a.title) - chapterNumber(b.title),
        );
        const withContent = sorted
          .map((c, idx) => ({ c, num: idx + 1 }))
          .filter(({ c }) => (c.content_length || 0) > 0);

        const details = await Promise.all(
          withContent.map(({ c }) => getChapter(p.phone, c.title)),
        );

        setChapters(
          withContent.map(({ c, num }, i) => ({
            num,
            summary: c,
            detail: details[i],
          })),
        );
        setError(null);
      } catch (e: unknown) {
        setError(
          e instanceof Error ? e.message : "Konnte das Buch nicht laden.",
        );
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <FullPageLoading />;

  function buildPlainText(): string {
    const lines: string[] = [];
    lines.push(name ? `Lebensgeschichte von ${name}` : "Lebensgeschichte");
    lines.push("");
    for (const c of chapters) {
      lines.push(
        `${toRoman(c.num)} — ${
          chapterTitleWithoutNumber(c.detail.title) || c.detail.title
        }`,
      );
      lines.push("");
      lines.push((c.detail.content || "").trim());
      lines.push("");
      lines.push("");
    }
    return lines.join("\n").trim();
  }

  async function copyAll() {
    try {
      await navigator.clipboard.writeText(buildPlainText());
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* ignore */
    }
  }

  const empty = chapters.length === 0;

  return (
    <AppShell
      eyebrow="Mein Buch · Text"
      title={
        <>
          Der ganze <em>Text</em>
        </>
      }
    >
      <div style={{ padding: "0 22px 90px" }}>
        {error && (
          <div
            role="alert"
            style={{
              background: "rgba(184,52,40,0.08)",
              border: "1px solid rgba(184,52,40,0.30)",
              borderRadius: 8,
              padding: "12px 14px",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 14,
              color: "var(--error)",
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <p
            style={{
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 14,
              color: "var(--ink-faint)",
              margin: 0,
            }}
          >
            {empty
              ? "Sobald Kapitel Inhalt haben, erscheinen sie hier."
              : `${chapters.length} Kapitel mit Inhalt`}
          </p>
          {!empty && (
            <button
              type="button"
              onClick={copyAll}
              className="btn-secondary"
              style={{
                padding: "6px 12px",
                minHeight: 0,
                fontSize: 12,
                whiteSpace: "nowrap",
              }}
            >
              {copied ? "Kopiert ✓" : "Alles kopieren"}
            </button>
          )}
        </div>

        {empty ? (
          <div
            style={{
              background: "var(--paper-50)",
              border: "1px solid rgba(43,26,15,0.10)",
              borderRadius: 12,
              padding: "20px 22px",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 15,
              color: "var(--ink-soft)",
            }}
          >
            Noch keine Kapitel mit Inhalt. Sobald die ersten Anrufe geführt
            sind, wächst hier der Text.
          </div>
        ) : (
          chapters.map((c) => (
            <article
              key={c.detail.title}
              style={{
                background: "var(--paper-50)",
                border: "1px solid rgba(43,26,15,0.10)",
                borderRadius: 4,
                padding: "26px 24px 22px",
                marginBottom: 22,
                boxShadow: "0 10px 22px -12px rgba(43,26,15,0.14)",
              }}
            >
              <div style={{ marginBottom: 14 }}>
                <div
                  style={{
                    fontFamily: "var(--f-display)",
                    fontStyle: "italic",
                    fontSize: 32,
                    lineHeight: 1,
                    color: "var(--oxblood)",
                    marginBottom: 4,
                  }}
                >
                  {toRoman(c.num)}
                </div>
                <h2
                  style={{
                    fontFamily: "var(--f-display)",
                    fontWeight: 500,
                    fontSize: 20,
                    margin: 0,
                    color: "var(--ink)",
                  }}
                >
                  {chapterTitleWithoutNumber(c.detail.title) || c.detail.title}
                </h2>
              </div>

              {(c.detail.content || "")
                .split(/\n\s*\n+/g)
                .map((p) => p.trim())
                .filter(Boolean)
                .map((p, i) => (
                  <p
                    key={i}
                    style={{
                      fontFamily: "var(--f-body)",
                      fontSize: 16,
                      lineHeight: 1.55,
                      color: "var(--ink)",
                      margin: "0 0 14px",
                    }}
                  >
                    {p}
                  </p>
                ))}
            </article>
          ))
        )}

        {!empty && (
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <Link
              to="/app/book"
              style={{
                fontFamily: "var(--f-ui)",
                fontSize: 12,
                color: "var(--ink-faint)",
                textDecoration: "underline",
                textUnderlineOffset: 3,
              }}
            >
              Zurück zur Buchseite
            </Link>
          </div>
        )}
      </div>
    </AppShell>
  );
}
